import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { dashboardAPI, tauxAPI, facturesAPI } from '../lib/api';
import { 
  Calculator, 
  TrendingUp, 
  AlertTriangle, 
  Info, 
  Banknote, 
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { TvaSeuilsProgress } from '../components/TvaSeuils';

const TVAPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [taux, setTaux] = useState(null);
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Préférences TVA
  const [typeActivite, setTypeActivite] = useState(user?.type_activite === 'BIC_VENTE' ? 'biens' : 'services'); // 'services' ou 'biens'
  const [optionDebits, setOptionDebits] = useState(false); // Option TVA sur les débits

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, tauxRes, facturesRes] = await Promise.all([
        dashboardAPI.getStats(),
        tauxAPI.get(),
        facturesAPI.getAll()
      ]);
      setStats(statsRes.data);
      setTaux(tauxRes.data);
      setFactures(facturesRes.data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Calcul TVA selon le mode choisi
  const calculTVA = () => {
    if (!factures.length) return { collectee: 0, aReverser: 0, detail: [] };

    let tvaCollectee = 0;
    let tvaAReverser = 0;
    const detail = [];

    factures.forEach(f => {
      const tvaMontant = f.montant_ttc - f.montant_ht;
      
      // Déterminer si TVA due
      let tvaDue = false;
      let raison = '';

      if (typeActivite === 'services') {
        if (optionDebits) {
          // TVA sur les débits = à la facturation
          tvaDue = true;
          raison = 'À la facturation (option débits)';
        } else {
          // TVA à l'encaissement
          tvaDue = f.statut === 'payee';
          raison = f.statut === 'payee' ? 'Encaissée' : 'Non due (attente paiement)';
        }
      } else {
        // Biens = à la livraison (donc à la facturation)
        tvaDue = true;
        raison = 'À la livraison/facturation';
      }

      tvaCollectee += tvaMontant;
      if (tvaDue) {
        tvaAReverser += tvaMontant;
      }

      detail.push({
        ...f,
        tvaMontant,
        tvaDue,
        raison
      });
    });

    return { 
      collectee: tvaCollectee, 
      aReverser: tvaAReverser,
      nonEncoreExigible: tvaCollectee - tvaAReverser,
      detail 
    };
  };

  const tvaData = calculTVA();
  const tvaDeductible = stats?.tva?.deductible || 0;
  const tvaAPayer = Math.max(0, tvaData.aReverser - tvaDeductible);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tva-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">TVA</h1>
        <p className="text-muted-foreground">Suivi de votre TVA collectée et à reverser</p>
      </div>

      {/* Plafonds de franchise TVA */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            Plafonds de franchise en base de TVA (2026)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TvaSeuilsProgress ca={Number(stats?.chiffre_affaires || 0)} typeActivite={typeActivite} isTvaSubject={!!user?.is_tva_subject} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-white rounded-lg border border-amber-200">
              <p className="text-sm text-gray-600 mb-1">Prestations de services et professions libérales (BIC/BNC)</p>
              <p className="text-2xl font-bold text-amber-700" data-testid="tva-seuil-services">37 500 €</p>
              <p className="text-xs text-gray-500 mt-1">Seuil majoré : 41 250 €</p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-amber-200">
              <p className="text-sm text-gray-600 mb-1">Vente de marchandises et hébergement</p>
              <p className="text-2xl font-bold text-amber-700" data-testid="tva-seuil-ventes">85 000 €</p>
              <p className="text-xs text-gray-500 mt-1">Seuil majoré : 93 500 €</p>
            </div>
          </div>
          <div className="flex items-start gap-2 p-3 bg-white rounded-lg border border-amber-200">
            <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-gray-700"><strong>Franchise en base :</strong> vous en bénéficiez si votre CA de l&apos;année précédente ne dépasse pas le seuil de base et si votre CA de l&apos;année en cours ne dépasse pas le seuil majoré.</p>
              <p className="text-gray-500 mt-1">Dépassement du seuil de base : TVA applicable à partir du 1er janvier de l&apos;année suivante. Dépassement du seuil majoré : TVA applicable dès le 1er jour de dépassement. Seuils 2026 inchangés par rapport à 2025.</p>
              <a 
                href="https://entreprendre.service-public.gouv.fr/vosdroits/F21746" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 font-medium mt-2"
              >
                <FileText className="h-4 w-4" />
                Consulter la fiche officielle (Service Public Entreprendre)
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration TVA */}
      <Card className="border-primary/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5" />
            Votre régime de TVA
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Type d'activité */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Type d'activité principale</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setTypeActivite('services')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  typeActivite === 'services'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FileText className={`h-5 w-5 ${typeActivite === 'services' ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-semibold">Prestations de services</p>
                    <p className="text-sm text-muted-foreground">Conseil, formation, développement...</p>
                  </div>
                </div>
                {typeActivite === 'services' && (
                  <Badge className="mt-2 bg-blue-100 text-blue-800">
                    TVA à l'encaissement (par défaut)
                  </Badge>
                )}
              </button>

              <button
                type="button"
                onClick={() => { setTypeActivite('biens'); setOptionDebits(false); }}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  typeActivite === 'biens'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Banknote className={`h-5 w-5 ${typeActivite === 'biens' ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-semibold">Vente de biens</p>
                    <p className="text-sm text-muted-foreground">E-commerce, achat-revente...</p>
                  </div>
                </div>
                {typeActivite === 'biens' && (
                  <Badge className="mt-2 bg-emerald-100 text-emerald-800">
                    TVA à la livraison
                  </Badge>
                )}
              </button>
            </div>
          </div>

          {/* Option débits (uniquement pour services) */}
          {typeActivite === 'services' && (
            <div className="p-4 bg-muted/50 rounded-lg border">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="option-debits"
                  checked={optionDebits}
                  onChange={(e) => setOptionDebits(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />
                <div>
                  <Label htmlFor="option-debits" className="font-semibold cursor-pointer">
                    J'ai opté pour la TVA sur les débits
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Avec cette option, la TVA est due dès la <strong>facturation</strong>, même si le client n'a pas encore payé.
                  </p>
                  {optionDebits && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                      Attention : vous devrez reverser la TVA avant d'être payé(e) !
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Résumé du régime */}
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <p className="font-medium text-primary flex items-center gap-2">
              <Info className="h-4 w-4" />
              Votre régime actuel
            </p>
            <p className="mt-1">
              {typeActivite === 'services' && !optionDebits && (
                <span>TVA due à l'<strong>encaissement</strong> : vous reversez la TVA uniquement quand vous êtes payé(e).</span>
              )}
              {typeActivite === 'services' && optionDebits && (
                <span>TVA due à la <strong>facturation</strong> : vous reversez la TVA dès l'émission de la facture.</span>
              )}
              {typeActivite === 'biens' && (
                <span>TVA due à la <strong>livraison</strong> : vous reversez la TVA à la date de livraison (ou facturation).</span>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats TVA */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">TVA collectée (total)</p>
            <p className="text-2xl font-bold font-['Manrope']">{formatCurrency(tvaData.collectee)}</p>
            <p className="text-xs text-muted-foreground">Sur toutes vos factures</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-amber-600" />
              <p className="text-sm text-amber-700 font-medium">TVA exigible</p>
            </div>
            <p className="text-2xl font-bold font-['Manrope'] text-amber-800">{formatCurrency(tvaData.aReverser)}</p>
            <p className="text-xs text-amber-600">Selon votre régime</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <p className="text-sm text-emerald-700 font-medium">TVA déductible</p>
            </div>
            <p className="text-2xl font-bold font-['Manrope'] text-emerald-800">{formatCurrency(tvaDeductible)}</p>
            <p className="text-xs text-emerald-600">Sur vos achats</p>
          </CardContent>
        </Card>
        <Card className={tvaAPayer > 0 ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}>
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-1">
              {tvaAPayer > 0 ? 'TVA à payer' : 'Crédit de TVA'}
            </p>
            <p className={`text-2xl font-bold font-['Manrope'] ${tvaAPayer > 0 ? 'text-red-800' : 'text-emerald-800'}`}>
              {formatCurrency(Math.abs(tvaAPayer))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Info non encore exigible */}
      {tvaData.nonEncoreExigible > 0 && typeActivite === 'services' && !optionDebits && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900">TVA non encore exigible</p>
                <p className="text-blue-800">
                  <strong>{formatCurrency(tvaData.nonEncoreExigible)}</strong> de TVA collectée sur factures non payées.
                  <br />
                  <span className="text-sm">Cette TVA sera à reverser uniquement quand vos clients vous paieront.</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Détail par facture */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Détail TVA par facture
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold">Facture</th>
                  <th className="text-left p-3 font-semibold">Client</th>
                  <th className="text-left p-3 font-semibold">Date</th>
                  <th className="text-right p-3 font-semibold">Montant HT</th>
                  <th className="text-right p-3 font-semibold">TVA</th>
                  <th className="text-center p-3 font-semibold">Paiement</th>
                  <th className="text-center p-3 font-semibold">TVA due ?</th>
                </tr>
              </thead>
              <tbody>
                {tvaData.detail.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-muted-foreground">
                      Aucune facture
                    </td>
                  </tr>
                ) : (
                  tvaData.detail.map((f) => (
                    <tr key={f.id} className="border-b hover:bg-muted/50">
                      <td className="p-3 font-mono">{f.numero}</td>
                      <td className="p-3">{f.client_name}</td>
                      <td className="p-3 text-muted-foreground">
                        {new Date(f.date_creation).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-3 text-right">{formatCurrency(f.montant_ht)}</td>
                      <td className="p-3 text-right font-semibold">{formatCurrency(f.tvaMontant)}</td>
                      <td className="p-3 text-center">
                        {f.statut === 'payee' ? (
                          <Badge className="bg-emerald-100 text-emerald-800">
                            <CheckCircle className="h-3 w-3 mr-1" /> Payée
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800">
                            <Clock className="h-3 w-3 mr-1" /> En attente
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {f.tvaDue ? (
                          <Badge className="bg-red-100 text-red-800">
                            <AlertTriangle className="h-3 w-3 mr-1" /> Oui
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">
                            Non
                          </Badge>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">{f.raison}</p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Rappel */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Rappel important</p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong>Services</strong> : TVA à l'encaissement par défaut (sauf option débits)
                </li>
                <li>
                  <strong>Biens</strong> : TVA à la livraison
                </li>
                <li>
                  Consultez la page <a href="/regles" className="text-primary underline">Règles TVA vs URSSAF</a> pour plus de détails
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TVAPage;
